import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Typoperation = {
    id: number;
    namn: string;
    [key: string]: any;
};

export type TypoperationDisplay = {
    value: Typoperation["id"];
    label: string;
};

type State = BaseState & {
    typoperationer: Map<Typoperation["id"], Typoperation>;
};

export const convertTypoperationData: (args: { typoperation?: Typoperation }) => TypoperationDisplay | null = ({
    typoperation,
}) => {
    if (!typoperation) {
        return null;
    }

    return {
        value: typoperation.id,
        label: typoperation.namn,
        ...typoperation,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        typoperationer: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "typoperationer";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        typoperationer: (state: State) => [...state.typoperationer.values()],
        typoperationerSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertTypoperationData({ typoperation: singleResult }));
            }

            return result;
        },
        typoperationById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.typoperationer.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addTypoperationer: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                typoperationer = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                typoperationer: Typoperation[];
            }
        ) =>
            subsetSetter<State, Typoperation>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: typoperationer,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchTypoperationer: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Typoperation>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "operation/typoperationer/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addTypoperationer", {
                    subsetName,
                    offset: resultOffset,
                    typoperationer: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertTypoperationData({ typoperation: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
